import React, { useState, useEffect } from 'react';
import {
  fetchUserAttributes,
  updateUserAttributes,
  updatePassword,
  setUpTOTP,
  verifyTOTPSetup,
  fetchMFAPreference,
  updateMFAPreference,
  fetchAuthSession,
} from 'aws-amplify/auth';
import { GROUP_LABELS, GROUP_COLORS } from '../utils/groups';
import { Button, Input, PasswordInput, FieldRow, AlertBanner, Badge, GroupBadge, PageHeader } from './ui';

const TABS = [
  { id: 'personal', label: 'Personal Info' },
  { id: 'security', label: 'Security' },
  { id: 'access',   label: 'Access' },
];

// ── Shared input class ────────────────────────────────────────────────────────
const inputClass = [
  'w-full px-3.5 py-2.5 rounded-[var(--radius-lg)]',
  'border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text-primary)]',
  'text-sm placeholder-[var(--color-text-muted)]',
  'focus:outline-none focus:ring-2 focus:ring-[var(--color-orange)]/20 focus:border-[var(--color-orange)]',
  'transition-colors',
].join(' ');

// ── Password field with show/hide ─────────────────────────────────────────────
function PasswordField({ label, value, onChange, show, onToggle, placeholder }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-[10px] font-semibold text-[var(--color-text-muted)] uppercase tracking-wider">{label}</label>
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder || '••••••••'}
          className={`${inputClass} pr-10`}
        />
        <button
          type="button"
          onClick={onToggle}
          tabIndex={-1}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] transition-colors"
        >
          {show ? (
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
}

// ── Personal tab ──────────────────────────────────────────────────────────────
function PersonalTab({ firstName, setFirstName, lastName, setLastName, email, setEmail }) {
  const [loading, setLoading] = useState(false);
  const [status,  setStatus]  = useState(null);

  async function handleSave() {
    setLoading(true); setStatus(null);
    try {
      await updateUserAttributes({
        userAttributes: {
          given_name:  firstName.trim(),
          family_name: lastName.trim(),
          email:       email.trim(),
        },
      });
      const current      = await fetchUserAttributes();
      const emailChanged = current.email !== email.trim();
      setStatus({
        type: 'success',
        message: emailChanged
          ? 'Profile updated. Check your inbox to verify your new email address before it takes effect.'
          : 'Profile updated successfully.',
      });
    } catch (err) {
      setStatus({ type: 'error', message: err.message || 'Failed to save changes.' });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <label className="text-[10px] font-semibold text-[var(--color-text-muted)] uppercase tracking-wider">First Name</label>
          <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="First name" className={inputClass} />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[10px] font-semibold text-[var(--color-text-muted)] uppercase tracking-wider">Last Name</label>
          <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Last name" className={inputClass} />
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <label className="text-[10px] font-semibold text-[var(--color-text-muted)] uppercase tracking-wider">Email Address</label>
        <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@uiaa217.org" className={inputClass} />
      </div>
      {status && <AlertBanner type={status.type}>{status.message}</AlertBanner>}
      <Button variant="primary" loading={loading} onClick={handleSave}>Save Changes</Button>
    </div>
  );
}

// ── Security tab ──────────────────────────────────────────────────────────────
function SecurityTab({ email, defaultSetupOpen = false }) {
  const [currentPw,   setCurrentPw]   = useState('');
  const [newPw,       setNewPw]       = useState('');
  const [confirmPw,   setConfirmPw]   = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew,     setShowNew]     = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pwLoading,   setPwLoading]   = useState(false);
  const [pwStatus,    setPwStatus]    = useState(null);

  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaLoading, setMfaLoading] = useState(true);
  const [mfaStatus,  setMfaStatus]  = useState(null);
  const [mfaStep,    setMfaStep]    = useState('idle');
  const [qrCode,     setQrCode]     = useState('');
  const [totpCode,   setTotpCode]   = useState('');

  useEffect(() => {
    fetchMFAPreference()
      .then(pref => {
        setMfaEnabled(pref.preferred === 'TOTP');
        // If redirected here from login MFA nudge, auto-start setup.
        // 1000ms delay gives Cognito time to settle after the previous
        // fetchMFAPreference call in Login.js before we call setUpTOTP.
        if (defaultSetupOpen && pref.preferred !== 'TOTP') {
          setTimeout(() => handleStartMfaSetup(), 1000);
        }
      })
      .catch(console.error)
      .finally(() => setMfaLoading(false));
  }, []); // eslint-disable-line

  async function handleChangePassword() {
    setPwStatus(null);
    if (newPw !== confirmPw) { setPwStatus({ type: 'error', message: 'New passwords do not match.' }); return; }
    if (newPw.length < 8)    { setPwStatus({ type: 'error', message: 'New password must be at least 8 characters.' }); return; }
    setPwLoading(true);
    try {
      await updatePassword({ oldPassword: currentPw, newPassword: newPw });
      setPwStatus({ type: 'success', message: 'Password updated successfully.' });
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
    } catch (err) {
      setPwStatus({ type: 'error', message: err.message || 'Failed to update password.' });
    } finally { setPwLoading(false); }
  }

  async function handleStartMfaSetup() {
    setMfaLoading(true); setMfaStatus(null);
    try {
      const totpSetup = await setUpTOTP();
      const uri = totpSetup.getSetupUri('Project 217 — UIAA', email);
      setQrCode(`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(uri.toString())}`);
      setMfaStep('setup');
    } catch (err) {
      const msg = err.message || '';
      setMfaStatus({
        type: 'error',
        message: msg.includes('Only one request')
          ? 'Please wait a moment and try again.'
          : msg || 'Failed to start MFA setup.',
      });
    } finally { setMfaLoading(false); }
  }

  async function handleConfirmMfa() {
    setMfaLoading(true); setMfaStatus(null);
    try {
      await verifyTOTPSetup({ code: totpCode });
      await updateMFAPreference({ totp: 'PREFERRED' });
      setMfaEnabled(true); setMfaStep('idle'); setTotpCode('');
      setMfaStatus({ type: 'success', message: 'Two-factor authentication enabled.' });
      // Clear the redirect flag from sessionStorage if present
      sessionStorage.removeItem('redirectAfterLogin');
    } catch {
      setMfaStatus({ type: 'error', message: 'Invalid code — please try again.' });
    } finally { setMfaLoading(false); }
  }

  async function handleDisableMfa() {
    setMfaLoading(true); setMfaStatus(null);
    try {
      await updateMFAPreference({ totp: 'DISABLED' });
      setMfaEnabled(false); setMfaStep('idle');
      setMfaStatus({ type: 'success', message: 'Two-factor authentication disabled.' });
    } catch (err) {
      setMfaStatus({ type: 'error', message: err.message || 'Failed to disable MFA.' });
    } finally { setMfaLoading(false); }
  }

  const sectionLabelClass = 'text-sm font-semibold text-[var(--color-text-primary)]';

  return (
    <div className="space-y-8">

      {/* Change Password */}
      <div>
        <h3 className={sectionLabelClass + ' mb-4'}>Change Password</h3>
        <div className="space-y-4">
          <PasswordField label="Current Password"     value={currentPw} onChange={setCurrentPw} show={showCurrent} onToggle={() => setShowCurrent(v => !v)} />
          <PasswordField label="New Password"         value={newPw}     onChange={setNewPw}     show={showNew}     onToggle={() => setShowNew(v => !v)}     placeholder="Min. 8 characters" />
          <PasswordField label="Confirm New Password" value={confirmPw} onChange={setConfirmPw} show={showConfirm} onToggle={() => setShowConfirm(v => !v)} />
        </div>
        {pwStatus && <AlertBanner type={pwStatus.type} className="mt-4">{pwStatus.message}</AlertBanner>}
        <div className="pt-4">
          <Button variant="primary" loading={pwLoading} onClick={handleChangePassword}>Update Password</Button>
        </div>
      </div>

      <div className="border-t border-[var(--color-border)]" />

      {/* Two-Factor Authentication */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <h3 className={sectionLabelClass}>Two-Factor Authentication</h3>
          {!mfaLoading && (
            <Badge variant={mfaEnabled ? 'success' : 'default'} dot>
              {mfaEnabled ? 'Enabled' : 'Not enabled'}
            </Badge>
          )}
        </div>
        <p className="text-xs text-[var(--color-text-muted)] mb-5 leading-relaxed">
          Generate a 6-digit code from your authenticator app each time you sign in. Works with{' '}
          <span className="text-[var(--color-text-secondary)] font-medium">Duo</span>,{' '}
          <span className="text-[var(--color-text-secondary)] font-medium">Google Authenticator</span>, and{' '}
          <span className="text-[var(--color-text-secondary)] font-medium">Authy</span>.
        </p>

        {mfaStep === 'idle' && !mfaEnabled && (
          <Button variant="primary" loading={mfaLoading} onClick={handleStartMfaSetup}>
            Set up authenticator app
          </Button>
        )}

        {mfaStep === 'setup' && (
          <div className="space-y-4">
            <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-[var(--radius-xl)] p-5 space-y-3">
              <p className="text-[10px] font-semibold text-[var(--color-orange)] uppercase tracking-wider">Step 1 — Scan QR Code</p>
              <p className="text-sm text-[var(--color-text-secondary)]">Open your authenticator app and scan this code.</p>
              <div className="flex justify-center py-2">
                <img src={qrCode} alt="MFA QR code" className="rounded-[var(--radius-lg)] border border-[var(--color-border)] p-2 bg-white" />
              </div>
            </div>
            <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-[var(--radius-xl)] p-5 space-y-3">
              <p className="text-[10px] font-semibold text-[var(--color-orange)] uppercase tracking-wider">Step 2 — Enter Code</p>
              <p className="text-sm text-[var(--color-text-secondary)]">Enter the 6-digit code from your app to confirm setup.</p>
              <input
                type="text"
                value={totpCode}
                onChange={e => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength={6}
                className={`${inputClass} tracking-[0.4em] text-center font-mono text-lg w-36`}
              />
            </div>
            <div className="flex gap-3">
              <Button variant="primary" loading={mfaLoading} disabled={totpCode.length !== 6} onClick={handleConfirmMfa}>
                Confirm &amp; Enable
              </Button>
              <Button variant="secondary" onClick={() => { setMfaStep('idle'); setTotpCode(''); setQrCode(''); }}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {mfaStep === 'idle' && mfaEnabled && (
          <Button variant="danger" loading={mfaLoading} onClick={handleDisableMfa}>
            {mfaLoading ? 'Disabling...' : 'Disable two-factor authentication'}
          </Button>
        )}

        {mfaStatus && <AlertBanner type={mfaStatus.type} className="mt-4">{mfaStatus.message}</AlertBanner>}
      </div>
    </div>
  );
}

// ── Access tab ────────────────────────────────────────────────────────────────
function AccessTab({ groups }) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-[var(--color-text-muted)] leading-relaxed">
        Your portal access is managed by an administrator. Contact them if you need changes.
      </p>
      {groups.length === 0 ? (
        <p className="text-sm text-[var(--color-text-muted)]">No groups assigned.</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {groups.map(g => (
            <GroupBadge key={g} group={g} groupLabels={GROUP_LABELS} groupColors={GROUP_COLORS} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main Profile ──────────────────────────────────────────────────────────────
export default function Profile() {
  const [firstName,      setFirstName]      = useState('');
  const [lastName,       setLastName]       = useState('');
  const [email,          setEmail]          = useState('');
  const [groups,         setGroups]         = useState([]);
  const [initials,       setInitials]       = useState('');
  const [loadingProfile, setLoadingProfile] = useState(true);

  // Check if redirected from login MFA nudge
  const redirectTarget = sessionStorage.getItem('redirectAfterLogin') || '';
  const defaultTab     = redirectTarget.includes('tab=security') ? 'security' : 'personal';
  const autoSetupMfa   = redirectTarget.includes('tab=security');

  const [activeTab, setActiveTab] = useState(defaultTab);

  useEffect(() => {
    async function load() {
      try {
        const [attrs, session] = await Promise.all([
          fetchUserAttributes(),
          fetchAuthSession(),
        ]);
        const fn = attrs.given_name  || '';
        const ln = attrs.family_name || '';
        setFirstName(fn);
        setLastName(ln);
        setEmail(attrs.email || '');
        setInitials(
          fn && ln ? `${fn[0]}${ln[0]}`.toUpperCase()
          : fn     ? fn[0].toUpperCase()
          : (attrs.email || 'U')[0].toUpperCase()
        );
        const payload   = session.tokens?.idToken?.payload || {};
        const rawGroups = payload['cognito:groups'] || [];
        setGroups(Array.isArray(rawGroups) ? rawGroups : [rawGroups]);
      } catch (err) {
        console.error('Failed to load profile:', err);
      } finally {
        setLoadingProfile(false);
      }
    }
    load();
  }, []);

  const displayName = firstName || lastName ? `${firstName} ${lastName}`.trim() : 'No name set';

  if (loadingProfile) {
    return (
      <div className="h-full flex flex-col overflow-hidden bg-white">
        <div className="px-6 sm:px-8 py-5 border-b border-[var(--color-border)] shrink-0">
          <div className="h-5 w-32 rounded-[var(--radius-md)] bg-[var(--color-surface-alt)] animate-pulse mb-1.5" />
          <div className="h-3 w-48 rounded-[var(--radius-md)] bg-[var(--color-surface-alt)] animate-pulse" />
        </div>
        <div className="flex gap-6 p-6 flex-1 min-h-0">
          <div className="w-52 shrink-0 h-48 rounded-[var(--radius-xl)] bg-[var(--color-surface-alt)] animate-pulse hidden sm:block" />
          <div className="flex-1 space-y-3">
            {[...Array(4)].map((_, i) => <div key={i} className="h-10 rounded-[var(--radius-lg)] bg-[var(--color-surface-alt)] animate-pulse" />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden bg-white">

      <PageHeader
        title={displayName}
        subtitle={email}
        action={
          <div className="flex flex-wrap gap-1.5">
            {groups.map(g => (
              <GroupBadge key={g} group={g} groupLabels={GROUP_LABELS} groupColors={GROUP_COLORS} />
            ))}
          </div>
        }
      />

      <div className="flex gap-6 p-4 sm:p-6 flex-1 min-h-0 overflow-hidden bg-[var(--color-surface)]">

        {/* Left — profile card (hidden on mobile) */}
        <div className="hidden sm:block w-52 shrink-0">
          <div className="rounded-[var(--radius-xl)] border border-[var(--color-border)] overflow-hidden bg-white shadow-[var(--shadow-xs)]">
            <div className="h-14 bg-[var(--color-orange)]" />
            <div className="px-5 pb-5">
              <div
                className="w-14 h-14 rounded-[var(--radius-xl)] flex items-center justify-center text-white font-bold text-lg -mt-7 mb-3 border-4 border-white"
                style={{ background: 'linear-gradient(135deg, var(--color-navy) 0%, #1e4080 100%)', boxShadow: 'var(--shadow-md)' }}
              >
                {initials}
              </div>
              <p className="text-sm font-semibold text-[var(--color-text-primary)] leading-tight">{displayName}</p>
              <p className="text-xs text-[var(--color-text-muted)] mt-0.5 break-all">{email}</p>
            </div>
          </div>
        </div>

        {/* Right — tabbed form */}
        <div className="flex-1 rounded-[var(--radius-xl)] border border-[var(--color-border)] overflow-hidden flex flex-col bg-white shadow-[var(--shadow-xs)]">
          <div className="flex border-b border-[var(--color-border)] bg-[var(--color-surface)] px-4 sm:px-6 shrink-0">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={[
                  'px-4 py-3.5 text-sm font-medium transition-colors duration-[var(--transition-base)]',
                  'border-b-2 -mb-px',
                  activeTab === tab.id
                    ? 'text-[var(--color-orange)] border-[var(--color-orange)] bg-white'
                    : 'text-[var(--color-text-muted)] border-transparent hover:text-[var(--color-text-secondary)]',
                ].join(' ')}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="flex-1 overflow-y-auto p-5 sm:p-6">
            {activeTab === 'personal' && (
              <PersonalTab
                firstName={firstName} setFirstName={setFirstName}
                lastName={lastName}   setLastName={setLastName}
                email={email}         setEmail={setEmail}
              />
            )}
            {activeTab === 'security' && (
              <SecurityTab email={email} defaultSetupOpen={autoSetupMfa} />
            )}
            {activeTab === 'access' && <AccessTab groups={groups} />}
          </div>
        </div>
      </div>
    </div>
  );
}